import React, { useState, useEffect, useRef } from "react";
import "./styles.css";
import Box from "@mui/material/Box";
import { MySwal } from "../../services/utills";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Button, Tooltip, IconButton, Avatar } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Link } from "react-router-dom";
import { MdDeleteOutline } from "react-icons/md";
import { deleteBlockHour } from "../../store/crm/calender";
import StaffUpdateModal from "../../pages/configuration/clinic-settings/staff/StaffUpdateModal";
import Waveloading from "../../pages/loading/Waveloading";
import toast from "react-hot-toast";

// 36px per 15-minute slot → 144px per hour
const SLOT_H = 36;

// "11:30 AM" → minutes since midnight (for overlap detection)
const toMinutes = (t) => {
  const m = String(t || "").match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return null;
  let [, h, mn, p] = m;
  h = parseInt(h); mn = parseInt(mn); p = p.toUpperCase();
  if (p === "PM" && h !== 12) h += 12;
  if (p === "AM" && h === 12) h = 0;
  return h * 60 + mn;
};

// Appointments shorter than this still render at a minimum height (SLOT_H*3 =
// 45 min), so two close-but-non-overlapping appointments can still collide
// visually. Use this as the effective span for collision detection so anything
// that *looks* overlapping is split into side-by-side lanes.
const MIN_VISUAL_MINUTES = 45;

// Assign side-by-side "lanes" to appointments that overlap (visually) within a
// single staff column (Google-Calendar style), so overlapping appointments no
// longer stack on top of each other. Returns a Map keyed by the booking item
// with { lane, lanes } where `lanes` is the width division for its cluster.
const computeLaneLayout = (bookings = []) => {
  const map = new Map();
  const entries = bookings
    .map((item) => {
      const start = toMinutes(item?.start_time);
      let end = toMinutes(item?.end_time);
      if (start == null) return null;
      if (end == null || end <= start) end = start + 30; // default span
      // Effective end accounts for the minimum render height so close
      // appointments that visually collide are also split into lanes.
      const effEnd = Math.max(end, start + MIN_VISUAL_MINUTES);
      return { item, start, end: effEnd, lane: 0, lanes: 1 };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  let cluster = [];
  let laneEnds = [];
  let clusterEnd = -1;

  const flush = () => {
    const lanesUsed = cluster.reduce((mx, e) => Math.max(mx, e.lane), 0) + 1;
    cluster.forEach((e) => map.set(e.item, { lane: e.lane, lanes: lanesUsed }));
    cluster = [];
    laneEnds = [];
    clusterEnd = -1;
  };

  entries.forEach((e) => {
    // A gap with the running cluster end → previous cluster is complete.
    if (cluster.length && e.start >= clusterEnd) flush();

    let placed = false;
    for (let l = 0; l < laneEnds.length; l++) {
      if (e.start >= laneEnds[l]) {
        e.lane = l;
        laneEnds[l] = e.end;
        placed = true;
        break;
      }
    }
    if (!placed) {
      e.lane = laneEnds.length;
      laneEnds.push(e.end);
    }
    cluster.push(e);
    clusterEnd = Math.max(clusterEnd, e.end);
  });
  if (cluster.length) flush();

  return map;
};

export const modalScroll = {
  "&::-webkit-scrollbar": { width: "10px", height: "10px" },
  "&::-webkit-scrollbar-track": { background: "#4D5862 !important" },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "4px",
    background: "#778899 !important",
    boxShadow: "inset 1px 1px 0px rgba(0,0,0,0.1), inset 0px -1px 0px rgba(0,0,0,0.07)",
  },
};

const CrmDayView = ({
  setOpenAppointmentDetail,
  dayViewData,
  startDate,
  minStartTime,
  maxEndTime,
  blockHours,
  onRescheduleSuccess,
  crmStaffs,
  selectedStaff,
}) => {
  const { user } = useSelector((state) => state?.authStore);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [currentTimeTop, setCurrentTimeTop] = useState(null);
  const [showFullText, setShowFullText] = useState([]);
  const scrollRef = useRef(null);
  const MAX_LENGTH = 30;

  const scrollColumns = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
    }
  };

  const handleClick = (index) => {
    const next = [...showFullText];
    next[index] = !next[index];
    setShowFullText(next);
  };

  const getDisplayText = (text, index) =>
    showFullText[index] || text.length <= 15 ? text : `${text.slice(0, 15)}...`;

  const deleteusercard = async (item) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "Do you want to delete?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#A72C68",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, delete it",
    });
    if (result.isConfirmed) {
      setLoading(true);
      try {
        const res = await dispatch(deleteBlockHour(item?.id));
        onRescheduleSuccess();
        if (res?.data?.success === "true") toast.success(res?.data?.message, { duration: 3000 });
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    }
  };

  // ── Time slot generation ──
  const generateTimeSlots = () => {
    const timeSlots = [];
    const dayIdx = startDate.getDay();
    if (!user?.business_hours?.[dayIdx]) return timeSlots;

    const dataLen = Array.isArray(dayViewData) ? dayViewData.length : 0;
    let startTimeParts, endTimeParts;

    // If appointments exist with known bounds, zoom in to only that window.
    // Fall back to full business hours when there are no appointments.
    if (dataLen > 0 && minStartTime && maxEndTime) {
      startTimeParts = minStartTime.match(/(\d+):(\d+) (AM|PM)/);
      endTimeParts = maxEndTime.match(/(\d+):(\d+) (AM|PM)/);
    } else if (user.business_hours[dayIdx]?.is_off === 0) {
      startTimeParts = user.business_hours[dayIdx].start_time.match(/(\d+):(\d+) (AM|PM)/);
      endTimeParts = user.business_hours[dayIdx].end_time.match(/(\d+):(\d+) (AM|PM)/);
    }

    if (!startTimeParts || !endTimeParts) return timeSlots;

    let [, sH, sM, sP] = startTimeParts;
    let [, eH, eM, eP] = endTimeParts;
    sH = parseInt(sH); sM = parseInt(sM);
    eH = parseInt(eH); eM = parseInt(eM);
    if (sP === "PM" && sH !== 12) sH += 12;
    if (eP === "PM" && eH !== 12) eH += 12;

    // Add 30-min buffer so first/last appointments are not flush against the edge
    if (dataLen > 0 && minStartTime && maxEndTime) {
      const bufMins = 30;
      const s = Math.max(0, sH * 60 + sM - bufMins);
      const e = eH * 60 + eM + bufMins;
      sH = Math.floor(s / 60); sM = s % 60;
      eH = Math.floor(e / 60); eM = e % 60;
    }

    let cur = new Date(2000, 0, 1, sH, sM);
    const end = new Date(2000, 0, 1, eH, eM);
    while (cur <= end) {
      timeSlots.push(
        cur.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })
      );
      cur = new Date(cur.getTime() + 15 * 60 * 1000);
    }
    return timeSlots;
  };

  const timeSlots = generateTimeSlots();

  // ── Current time red-line position ──
  useEffect(() => {
    if (!timeSlots.length) return;
    const update = () => {l
      const m = timeSlots[0].match(/(\d+):(\d+) (AM|PM)/);
      if (!m) return;
      let [, h, min, p] = m;
      h = parseInt(h); min = parseInt(min);
      if (p === "PM" && h !== 12) h += 12;
      const startMins = h * 60 + min;
      const nowMins = new Date().getHours() * 60 + new Date().getMinutes();
      const diff = nowMins - startMins;
      setCurrentTimeTop(diff >= 0 ? (diff / 15) * SLOT_H : null);
    };
    update();
    const iv = setInterval(update, 60000);
    return () => clearInterval(iv);
  }, [timeSlots.length]);

  // ── Normalize dayViewData to always be an array ──
  const dataArray = Array.isArray(dayViewData) ? dayViewData : [];

  // ── Group bookings by column (president_level) ──
  const bookingsByColumn = {};
  dataArray.forEach((item) => {
    const col = item.president_level ?? 0;
    if (!bookingsByColumn[col]) bookingsByColumn[col] = [];
    bookingsByColumn[col].push(item);
  });

  const staffList = crmStaffs || [];
  const showAll = !selectedStaff || selectedStaff === "all";

  let columns;
  if (staffList.length > 0) {
    const visibleStaff = showAll
      ? staffList
      : staffList.filter((s) => s.id === selectedStaff);

    columns = visibleStaff.map((staff) => {
      const colIdx = staffList.indexOf(staff);
      return { staff, bookings: bookingsByColumn[colIdx] || [] };
    });
  } else {
    columns = [{ staff: null, bookings: dataArray }];
  }

  if (!columns.length) {
    columns = [{ staff: null, bookings: dataArray }];
  }

  // Every 4th slot = each full hour
  const hourLabels = timeSlots.filter((_, i) => i % 4 === 0);
  const totalH = timeSlots.length * SLOT_H;
  const HEADER_H = 84; // staff header height in px

  // Current time label string
  const currentTimeLabel = new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // ── No business hours fallback ──
  if (!timeSlots.length) {
    return (
      <Box sx={{ textAlign: "center", mt: 5, py: 6 }}>
        <Typography sx={{ fontFamily: "helvetica-lt-std-bold", fontSize: "1.2rem", fontWeight: "bold" }}>
          Business Hours Not Set
        </Typography>
        <Link to={`/crm/configuration/clinic/${user?.id}/business-hours`} style={{ textDecoration: "none", zIndex: 9999999 }}>
          <Button variant="contained" sx={{ mt: 2 }}>
            Select Business Hours
          </Button>
        </Link>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      {loading && <Waveloading fullSize />}

      {/* ── Staff scroll arrow bar ── */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "flex-end",
        px: { xs: 1, sm: 2 }, py: 1, borderBottom: "1px solid #eef0f4", gap: 1,
      }}>
        <Typography sx={{ fontSize: "13px", color: "#888", fontFamily: "helvetica-lt-std-roman", flex: 1 }}>
          {columns.length} staff member{columns.length !== 1 ? "s" : ""}
        </Typography>
        <IconButton
          size="small"
          onClick={() => scrollColumns("left")}
          sx={{
            background: "#FAEFF5", border: "1px solid #F0CDDB", borderRadius: "8px",
            color: "#A72C68", width: 34, height: 34,
            "&:hover": { background: "#F0CDDB" },
          }}
        >
          <KeyboardArrowLeftIcon sx={{ fontSize: "22px" }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => scrollColumns("right")}
          sx={{
            background: "#FAEFF5", border: "1px solid #F0CDDB", borderRadius: "8px",
            color: "#A72C68", width: 34, height: 34,
            "&:hover": { background: "#F0CDDB" },
          }}
        >
          <KeyboardArrowRightIcon sx={{ fontSize: "22px" }} />
        </IconButton>
      </Box>

      {/* ── Scrollable container ── */}
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: "6px" },
          "&::-webkit-scrollbar-track": { background: "#FAEFF5" },
          "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.15)", borderRadius: "4px" },
        }}
      >
        {/* ── LEFT: Time column (sticky) ── */}
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: "62px", sm: "64px" },
            borderRight: "1px solid #eef0f4",
            position: "sticky",
            left: 0,
            background: "#fff",
            zIndex: 10,
          }}
        >
          {/* GMT header */}
          <Box
            sx={{
              height: HEADER_H,
              borderBottom: "1px solid #eef0f4",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#aaa",
                fontFamily: "helvetica-lt-std-bold",
                lineHeight: 1.3,
              }}
            >
              GMT
            </Typography>
          </Box>

          {/* Hour labels + current-time badge */}
          <Box sx={{ position: "relative", height: totalH }}>
            {hourLabels.map((label, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  top: i * SLOT_H * 4,
                  width: "100%",
                  height: SLOT_H * 4,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-end",
                  pr: { xs: "4px", sm: "6px" },
                  pl: "2px",
                  pt: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "11px", sm: "13px" },
                    color: "#888",
                    fontFamily: "helvetica-lt-std-roman",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}

            {/* Current time badge in time column */}
            {currentTimeTop !== null && (
              <Box
                sx={{
                  position: "absolute",
                  top: currentTimeTop - 11,
                  right: 0,
                  background: "#1a1a2e",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                  fontFamily: "helvetica-lt-std-bold",
                  px: "6px",
                  py: "3px",
                  borderRadius: "6px 0 0 6px",
                  whiteSpace: "nowrap",
                  zIndex: 6,
                }}
              >
                {currentTimeLabel}
              </Box>
            )}
          </Box>
        </Box>

        {/* ── RIGHT: Staff columns ── */}
        <Box sx={{ display: "flex", flex: 1, position: "relative", minWidth: 0 }}>
          {columns.map((col, colIdx) => {
            // Side-by-side lanes for overlapping appointments in this column
            const laneLayout = computeLaneLayout(col.bookings);
            return (
            <Box
              key={colIdx}
              sx={{
                flexShrink: 0,
                width: { xs: "calc(100vw - 90px)", sm: "300px" },
                borderRight: "1px solid #eef0f4",
                position: "relative",
              }}
            >
              {/* Staff header */}
              <Box
                sx={{
                  height: HEADER_H,
                  borderBottom: "1px solid #eef0f4",
                  px: { xs: 1, sm: 1.5 },
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, sm: 1.5 },
                  background: "#fff",
                  overflow: "hidden",
                }}
              >
                {col.staff ? (
                  <>
                    <Avatar
                      src={col.staff.profile_image}
                      variant="rounded"
                      sx={{ width: { xs: 32, sm: 44 }, height: { xs: 32, sm: 44 }, flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "13px", sm: "16px" },
                          color: "#1a1a2e",
                          fontFamily: "helvetica-lt-std-bold",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                        }}
                      >
                        {col.staff.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "11px", sm: "13px" },
                          color: "#888",
                          fontFamily: "helvetica-lt-std-roman",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                          mt: "2px",
                        }}
                      >
                        {col.bookings.length} patient(s)
                      </Typography>
                    </Box>
                  </>
                ) : null}
              </Box>

              {/* Grid area */}
              <Box sx={{ position: "relative", height: totalH }}>
                {/* Hour grid lines */}
                {hourLabels.map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "absolute",
                      top: i * SLOT_H * 4,
                      left: 0,
                      right: 0,
                      height: SLOT_H * 4,
                      borderBottom: "1px solid #eef0f4",
                    }}
                  />
                ))}


                {/* Appointments */}
                {col.bookings.map((item, bi) => {
                  if (!item?.start_time || !item?.end_time) return null;
                  const si = timeSlots.indexOf(item.start_time);
                  const ei = timeSlots.indexOf(item.end_time);
                  if (si < 0) return null;
                  const topPx = si * SLOT_H;
                  const rawH = ei > 0 ? (ei - si) * SLOT_H : SLOT_H * 4;
                  const hPx = Math.max(rawH, SLOT_H * 3);

                  // Overlapping appointments share the column width across lanes
                  const { lane = 0, lanes = 1 } = laneLayout.get(item) || {};
                  const widthPct = 100 / lanes;
                  const leftCss = `calc(${lane * widthPct}% + 5px)`;
                  const widthCss = `calc(${widthPct}% - ${lanes > 1 ? 7 : 10}px)`;

                  return (
                    <Tooltip
                      key={bi}
                      title={item.customer?.name || "No Name"}
                      placement="top"
                      arrow
                    >
                    <Box
                      onClick={() => setOpenAppointmentDetail(item.id)}
                      sx={{
                        position: "absolute",
                        top: topPx,
                        left: leftCss,
                        width: widthCss,
                        height: hPx,
                        background: item.opacity_color || "#f0f9ff",
                        borderLeft: `4px solid ${item.status_color || "#A72C68"}`,
                        borderRadius: "6px",
                        p: lanes > 1 ? "6px 7px" : "7px 10px",
                        cursor: "pointer",
                        overflow: "hidden",
                        zIndex: 2,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                        transition: "left 0.15s, width 0.15s",
                        // On hover, lift a narrow (overlapped) card to full column
                        // width so its details become readable.
                        "&:hover": {
                          zIndex: 5,
                          opacity: 0.98,
                          ...(lanes > 1 && {
                            left: "5px",
                            width: "calc(100% - 10px)",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                          }),
                        },
                      }}
                    >
                      {/* Name + status badge */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "15px",
                            color: "#1a1a2e",
                            fontFamily: "helvetica-lt-std-bold",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                            pr: "4px",
                          }}
                        >
                          {item.customer?.name || "No Name"}
                        </Typography>
                        {item.status !== "CheckOut" && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                          {item.status === "Pending" ? (
                            <AccessTimeIcon sx={{ fontSize: "14px", color: item.status_color || "#56CCF2" }} />
                          ) : (
                            <Box
                              sx={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: item.status_color || "#ccc",
                              }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: item.status_color || "#666",
                              fontWeight: 600,
                              fontFamily: "helvetica-lt-std-roman",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.status}
                          </Typography>
                        </Box>
                        )}
                      </Box>

                      {/* Time range */}
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#555",
                          fontFamily: "helvetica-lt-std-roman",
                          mt: "2px",
                        }}
                      >
                        {item.start_time} › {item.end_time}
                      </Typography>

                      {/* Treatment chips */}
                      {item?.service_name?.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "3px", mt: "4px" }}>
                          {item.service_name.slice(0, 2).map((svc, si) => {
                            const txt = `${svc}${
                              item.type === "Booking"
                                ? ""
                                : item.type === "Consultation"
                                ? " (In Person)"
                                : " (Virtual)"
                            }`;
                            return (
                              <Box
                                key={si}
                                onClick={(e) => { e.stopPropagation(); handleClick(si); }}
                                sx={{
                                  background: "rgba(255,255,255,0.85)",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  borderRadius: "10px",
                                  px: "8px",
                                  py: "2px",
                                  fontSize: "12px",
                                  fontFamily: "helvetica-lt-std-roman",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                  cursor: "pointer",
                                  textTransform: "capitalize",
                                }}
                              >
                                {getDisplayText(txt, si)}
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {/* Appointment notes */}
                      {item?.appointment_customer_note &&
                        typeof item.appointment_customer_note === "object" &&
                        item.appointment_customer_note !== null && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#666",
                              fontFamily: "helvetica-lt-std-roman",
                              mt: "2px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: expanded ? "normal" : "nowrap",
                              cursor:
                                item.appointment_customer_note.description?.length > MAX_LENGTH
                                  ? "pointer"
                                  : "default",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.appointment_customer_note.description?.length > MAX_LENGTH)
                                setExpanded(!expanded);
                            }}
                          >
                            {expanded
                              ? item.appointment_customer_note.description
                              : item.appointment_customer_note.description?.slice(0, MAX_LENGTH) +
                                (item.appointment_customer_note.description?.length > MAX_LENGTH
                                  ? "..."
                                  : "")}
                          </Typography>
                        )}
                    </Box>
                    </Tooltip>
                  );
                })}

              </Box>
            </Box>
            );
          })}

          {/* Block hours (break time / unavailable) — a single hatched overlay
              spanning the FULL width of every staff column (not just the first),
              so a blocked time reads as clinic-wide. It sits behind appointment
              cards (zIndex 1 < appointment zIndex 2); the notes + edit/delete
              actions render at the left edge and stay clickable. */}
          {(blockHours || []).map((item, bi) => {
            if (!item?.start_time || !item?.end_time) return null;
            const si = timeSlots.indexOf(item.start_time);
            const ei = timeSlots.indexOf(item.end_time);
            if (si < 0) return null;
            const topPx = HEADER_H + si * SLOT_H;
            const hPx = (ei - si) * SLOT_H;

            return (
              <Box
                key={`block-${bi}`}
                sx={{
                  position: "absolute",
                  top: topPx,
                  left: 0,
                  // Cover every staff column: stretch to the full scroll width
                  // (columns × width) but never less than the visible area, so a
                  // single short column still fills the empty space to its right.
                  width: {
                    xs: `calc((100vw - 90px) * ${columns.length})`,
                    sm: `${columns.length * 300}px`,
                  },
                  minWidth: "100%",
                  height: hPx,
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                {/* Hatch fill */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "repeating-linear-gradient(45deg,#F4D5E2,#F4D5E2 6px,#E8B7CC 6px,#E8B7CC 12px)",
                    border: "1px solid #E8B7CC",
                  }}
                />
                {/* Notes pinned to the left edge, actions pinned to the right
                    edge — both stay visible while scrolling horizontally. */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    p: "8px 12px",
                    pointerEvents: "none",
                  }}
                >
                  <Box sx={{ position: "sticky", left: "12px", minWidth: 0, pointerEvents: "none" }}>
                    {item?.notes && (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#1a1a2e",
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Block Notes: {item.notes}
                      </Typography>
                    )}
                    <Typography
                      sx={{ mt: "2px", fontSize: "12px", fontWeight: 600, color: "#6B4156" }}
                    >
                      {item.start_time} - {item.end_time}
                    </Typography>
                  </Box>
                  <Box sx={{ position: "sticky", right: "12px", display: "flex", gap: "4px", flexShrink: 0, pointerEvents: "auto" }}>
                    <StaffUpdateModal id={item?.id} onRescheduleSuccess={onRescheduleSuccess} />
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => deleteusercard(item)}
                        sx={{
                          width: 26, height: 26,
                          background: "#fff",
                          border: "1px solid #F0CDDB",
                          "&:hover": { background: "#FFEAEA", borderColor: "#EB5757" },
                        }}
                      >
                        <MdDeleteOutline color="#E53E31" fontSize="16px" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CrmDayView;
